import { decodeHtmlEntities } from "./htmlEntities";

const KORTEX_STORAGE_GATE_BASE = "https://web.loykortex.com/api/storage/gate";

function normalizeTextValue(value) {
  if (value == null) return "";
  return decodeHtmlEntities(String(value)).trim();
}

function pickReasoning(item) {
  return (
    item?.reasoning ??
    item?.explanation ??
    item?.rationale ??
    item?.justification ??
    item?.justificativa ??
    item?.explicacao ??
    null
  );
}

/**
 * Converte `relevant_pages` em string quando vier como array/objeto da Kortex.
 */
export function coerceRelevantPagesField(value) {
  if (value == null) return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    const blocks = value
      .map((entry) => formatRelevantPagesEntry(entry))
      .filter(Boolean);
    return blocks.length > 0 ? blocks.join("\n\n") : null;
  }
  if (typeof value === "object") {
    return formatRelevantPagesEntry(value);
  }
  return String(value);
}

function formatRelevantPagesEntry(entry) {
  if (entry == null) return null;
  if (typeof entry === "string") return normalizeRelevantPagesText(entry);

  const uid =
    entry.uid ??
    entry.document_uid ??
    entry.documentUid ??
    entry.document_id ??
    entry.documentId ??
    null;
  const label =
    entry.label ??
    entry.document_type ??
    entry.documentType ??
    entry.type ??
    "Documento";
  const pagesRaw = entry.pages ?? entry.page ?? entry.page_numbers ?? entry.pageNumbers ?? "1";
  const pages = Array.isArray(pagesRaw)
    ? pagesRaw.map((p) => String(p).trim()).filter(Boolean).join(",")
    : String(pagesRaw).trim();

  if (uid) {
    return `${label}|${String(uid).trim()}\n${pages || "1"}`;
  }

  const url = entry.url ?? entry.document_url ?? entry.documentUrl;
  if (url) return String(url).trim();

  return null;
}

/**
 * Normaliza texto de páginas relevantes (quebras reais, literais \\n, etc.).
 */
export function normalizeRelevantPagesText(value) {
  if (value == null) return "";
  let text = normalizeTextValue(value);
  if (!text) return "";
  if (text.includes("\\n") && !text.includes("\n")) {
    text = text.replace(/\\n/g, "\n");
  }
  return text;
}

/**
 * Divide múltiplos blocos "tipo|uid\\npáginas" no mesmo campo.
 */
export function collectRelevantPagesItems(value) {
  const text = coerceRelevantPagesField(value);
  if (!text) return [];

  const normalized = normalizeRelevantPagesText(text);
  if (!normalized) return [];

  if (/^https?:\/\//i.test(normalized)) {
    return [normalized];
  }

  const blocks = normalized
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);

  if (blocks.length > 1) return blocks;

  return [normalized];
}

/**
 * Parseia string de páginas relevantes em links Loykortex.
 * Formato: "Decisão|uuid\\n1,2"
 */
export function parseRelevantPages(relevantPages) {
  const text = normalizeRelevantPagesText(relevantPages);
  if (!text) return null;

  if (/^https?:\/\//i.test(text)) {
    return [
      {
        label: "Documento",
        uid: null,
        page: null,
        url: text,
      },
    ];
  }

  try {
    const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (lines.length < 2) return null;

    const [label, uid] = lines[0].split("|");
    if (!uid?.trim()) return null;

    const pageNumbers = lines[1]
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    if (pageNumbers.length === 0) return null;

    return pageNumbers.map((pageNum) => ({
      label: label?.trim() || "Documento",
      uid: uid.trim(),
      page: pageNum,
      url: `${KORTEX_STORAGE_GATE_BASE}/${uid.trim()}#page=${pageNum}`,
    }));
  } catch {
    return null;
  }
}

export function parseAllRelevantPages(value) {
  return collectRelevantPagesItems(value).flatMap(
    (block) => parseRelevantPages(block) || [],
  );
}

export function normalizeKortexResultItem(item, fallbackQuestionText) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return item;

  const reasoning = pickReasoning(item);
  const relevantPages = coerceRelevantPagesField(
    item.relevant_pages ?? item.relevantPages ?? item.relevant_page,
  );

  return {
    ...item,
    question_text:
      item.question_text ?? item.questionText ?? fallbackQuestionText ?? null,
    document_type: item.document_type ?? item.documentType ?? item.doc_type ?? null,
    cnj: item.cnj ?? item.process_number ?? item.processNumber ?? null,
    header: item.header ?? item.csv_header ?? item.csvHeader ?? null,
    relevant_pages: relevantPages,
    documentDate:
      item.documentDate ??
      item.document_date ??
      item.document_date_time ??
      item.document_datetime ??
      null,
    movementDate:
      item.movementDate ??
      item.movement_date ??
      item.movement_date_time ??
      item.movement_datetime ??
      null,
    movement_id:
      item.movement_id ?? item.movementId ?? item.movementID ?? item.event_id ?? item.eventId ?? null,
    document_url: item.document_url ?? item.documentUrl ?? item.url ?? null,
    reasoning,
  };
}

/**
 * Expande um item de resultado em uma ou mais fontes (resposta + documento).
 */
export function buildSourcesFromKortexResultItem(item) {
  if (!item || typeof item !== "object") return [];

  const nested =
    item.sources ??
    item.document_sources ??
    item.documentSources ??
    item.documents;

  if (Array.isArray(nested) && nested.length > 0) {
    return nested.map((entry) => normalizeKortexSource(entry));
  }

  return [normalizeKortexSource(item)];
}

export function normalizeKortexSource(raw) {
  const relevantPagesItems = collectRelevantPagesItems(
    raw?.relevant_pages ?? raw?.relevantPages ?? raw?.relevant_page,
  );

  return {
    id: raw?.id ?? null,
    created_at: raw?.created_at ?? raw?.createdAt ?? null,
    document_type: raw?.document_type ?? raw?.documentType ?? raw?.doc_type ?? null,
    cnj: raw?.cnj ?? raw?.process_number ?? raw?.processNumber ?? null,
    documentDate:
      raw?.documentDate ??
      raw?.document_date ??
      raw?.document_date_time ??
      raw?.document_datetime ??
      null,
    movementDate:
      raw?.movementDate ??
      raw?.movement_date ??
      raw?.movement_date_time ??
      raw?.movement_datetime ??
      null,
    movement_id:
      raw?.movement_id ??
      raw?.movementId ??
      raw?.movementID ??
      raw?.event_id ??
      raw?.eventId ??
      null,
    header: raw?.header ?? raw?.csv_header ?? raw?.csvHeader ?? null,
    relevant_pages: relevantPagesItems[0] ?? null,
    relevant_pages_items: relevantPagesItems,
    document_url: raw?.document_url ?? raw?.documentUrl ?? raw?.url ?? null,
    answer: raw?.answer ?? raw?.value ?? raw?.text ?? null,
    reasoning: pickReasoning(raw),
  };
}

export function hasKortexDocumentLinks(source) {
  const answer = normalizeTextValue(source?.answer).toLowerCase();
  if (answer.includes("nenhum documento associado")) return false;

  if (source?.document_url) return true;

  const pageItems =
    source?.relevant_pages_items?.length > 0
      ? source.relevant_pages_items
      : source?.relevant_pages
        ? [source.relevant_pages]
        : [];

  return pageItems.some((raw) => {
    const text = normalizeRelevantPagesText(raw);
    if (!text) return false;
    if (parseAllRelevantPages(text).length > 0) return true;
    return text.includes("|") || /^https?:\/\//i.test(text);
  });
}
